<template>
  <!-- eslint-disable vue-a11y/label-has-for, vue-a11y/form-has-label, vue-a11y/no-onchange -->
  <FieldValidation
    #default="{ classes }"
    class="Field-cont"
    :class="{ active: value === radioValue }"
    :disabled="!rules && !isRequired"
    :name="name || label"
    :rules="rules">
    <FieldLabel
      v-if="(label || $slots.label) && !is('checkbox', 'radio')"
      :class="labelClass"
      :for="inputId"
      :label="label"
      :required="required" />
    <div class="Field" :class="[...fieldClasses, ...classes]">
      <Icon v-if="_icon" :name="_icon" />
      <span v-else-if="_iconText" class="Icon">
        {{ _iconText }}
      </span>
      <input
        v-if="isInput"
        ref="input"
        :id="inputId"
        v-bind="attrs"
        :step="step"
        :type="_type"
        :value="_value"
        @change="change"
        @focus="$emit('focus')"
        @focusout="$emit('focusout')"
        @input="set">
      <div v-else-if="is('checkbox')" class="checkbox">
        <FieldLabel :class="{ isSwitch }" :for="inputId">
          <input
            :id="inputId"
            v-bind="attrs"
            :checked="value ? 'checked' : ''"
            type="checkbox"
            :value="value"
            @change="set($event.target.checked)">
          <template v-if="$slots.label">
            <slot name="label" />
          </template>
          <template v-else>
            {{ label }}
          </template>
          <div class="slider" />
        </FieldLabel>
      </div>
      <template v-else-if="is('radio')">
        <FieldLabel :for="inputId">
          <input
            :id="inputId"
            v-bind="attrs"
            :checked="value ? 'checked' : ''"
            type="radio"
            :value="value"
            @change="set($event.target.checked)">
          <template v-if="$slots.label">
            <slot name="label" />
          </template>
          <template v-else>
            {{ label }}
          </template>
        </FieldLabel>
      </template>
      <textarea
        v-else-if="is('textarea')"
        ref="textarea"
        :id="inputId"
        v-bind="attrs"
        :value="_value"
        @change="change"
        @focus="$emit('focus')"
        @focusout="$emit('focusout')"
        @input="set($event)" />
      <select
        v-else-if="is('select')"
        ref="select"
        :id="inputId"
        v-bind="attrs"
        :value="_value"
        @blur="setSelectIcon('down')"
        @change="set($event)"
        @focus="setFocus(true)"
        @focusout="setFocus(false);setSelectIcon('down')"
        @mousedown="setSelectIcon('up')"
        @mouseup="setSelectIcon('down')">
        <option v-if="$attrs.placeholder" data-type="object" disabled :value="null">
          {{ $attrs.placeholder }}
        </option>
        <option
          v-for="option in optionsFormatted"
          :key="`${option.label}`"
          :data-type="(typeof option.value)"
          :value="option.value">
          {{ option.label }}
        </option>
      </select>
      <Btn v-if="canCopy" class="Field-copy sm ib success" @click="copy">
        {{ $t('misc.copy') }}
      </Btn>
      <slot />
    </div>
  </FieldValidation>
</template>

<script>
import { isNumeric, isObject } from '@utils/guards'
import FieldLike from '@mixins/FieldLike'
import { randomElementId } from '@utils/strings'

const filters = {
  numeric: {
    out: value => isNumeric(value) ? Number(value) : null,
  },
  select: {
    // TODO: generalize into an auto type casting function
    out: (value, type) => {
      if (type === 'string') return value
      if (value === null || value === '' || value === 'null') return null
      if (value === 'true') return true
      if (value === 'false') return false

      return isNaN(Number(value))
        ? value
        : Number(value)
    },
  },
}

const entryToOption = ([value, label]) => ({ label, value })

export default {
  mixins: [FieldLike],
  props: {
    canCopy: { type: Boolean, default: false },
    doExpand: { type: Boolean, default: true },
    icon: { type: String, default: null },
    iconText: { type: String, default: null },
    inputId: { type: String, default: randomElementId },
    isSwitch: { type: Boolean, default: false },
    options: { type: Array, default: () => [] },
    radioValue: { type: null, default: null },
    step: { type: [Number, String], default: null },
    type: { type: String, default: 'text' },
    value: { type: null, required: true },
  },
  data() {
    return {
      isFocused: false,
      selectIcon: 'down',
    }
  },
  computed: {
    attrs() {
      return {
        ...this.$attrs,
        'aria-label': this.ariaLabel,
        disabled: this.disabled,
        name: this.name,
        required: this.isRequired,
      }
    },
    ariaLabel() {
      const label = this.label ||
        this.$attrs.placeholder ||
        this.$attrs.ariaLabel ||
        this.$attrs['aria-label']

      if (typeof label !== 'string') return

      return label.replace('*', '').trim()
    },
    _icon() {
      return this.icon || {
        select: this.selectIcon,
      }[this.type]
    },
    _iconText() {
      return this.iconText || {
        money: '$',
      }[this.type]
    },
    _iconAlignment() {
      return {
        select: 'iconRight',
      }[this.type] || null
    },
    _type() {
      return {
        money: 'number',
      }[this.type] || this.type
    },
    _value() {
      return this.filterIn
        ? this.filterIn(this.value)
        : this.value
    },
    fieldClasses() {
      const conditions = [
        [this.disabled, 'disabled'],
        [this.isRequired, 'required'],
        [this.isFocused, 'focus'],
        [this.is('select'), 'trans'],
        [this._icon || this._iconText, 'withIcon'],
        [this.isInput, 'inp'],
        [this._icon && this._iconAlignment, this._iconAlignment],
      ]

      return [
        `type-${this._type}`,
        ...conditions
          .filter(([condition]) => condition)
          .map(([_, classes]) => classes),
      ]
    },
    filterIn() {
      return {
        // check: filters.check.in,
        // time: filters.time.in,
      }[this.type]
    },
    filterOut() {
      return {
        text: value => value || null,
        // check: filters.check.out,
        money: filters.numeric.out,
        number: filters.numeric.out,
        select: filters.select.out,
      }[this.type] || (value => value)
    },
    isInput() {
      return ['email', 'hidden', 'number', 'password', 'percent', 'text'].includes(this._type)
    },
    optionsFormatted() {
      if (Array.isArray(this.options)) {
        const optionFirst = this.options.find(option => option !== null)

        if (!optionFirst) return this.options

        return this.options.map((option) => {
          if (Array.isArray(option)) return entryToOption(option)
          if (isObject(option)) return option
          return { label: option, option }
        })
      } else if (isObject(this.options)) {
        // { value: label, ... }
        return Object
          .entries(this.options)
          .map(entryToOption)
      }

      return []
    },
  },
  methods: {
    change(event) {
      this.$emit('change', this.getEventValue(event))
    },
    copy() {
      this.$clipboard(this.value)
      this.$store.dispatch('SUCCESS', this.$t('misc.copied'))
    },
    getEventValue(event) {
      if (!isObject(event)) return this.filterOut(event)

      const dataType = event.target[0] && event.target[0].dataset.type

      return this.filterOut(event.target.value, dataType)
    },
    is(...types) {
      return types.some(type => this.type === type)
    },
    set(event) {
      const value = this.getEventValue(event)

      this.$emit('update:value', value)
      this.$emit('input', value)
    },
    setSelectIcon(value) {
      this.selectIcon = value
    },
    setFocus(value) {
      this.isFocused = value
    },
    autoExpand() {
      // grow height based on content
      if (!this.is('textarea') || this.doExpand === false) return

      const { textarea } = this.$refs

      if (!textarea) return

      const offset = textarea.style.height ? 0 : 5

      textarea.style.height = 'auto'
      textarea.style.height = `${textarea.scrollHeight + offset}px`
    },
  },
  watch: {
    value() {
      this.autoExpand()
    },
  },
  created() {
    this.$emit('update:inputId', this.inputId)
  },
  mounted() {
    this.autoExpand()
  },
}
</script>

<style lang="scss">
$_switch-bg: $info;

input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.Field-cont .FieldLabel {
  line-height: inherit;
}

.Field {
  position: relative;

  input,
  select,
  textarea,
  input[type="number"],
  &.withIcon {
    background: $white;
    border: 1px solid $border-color;
    border-radius: $radius-md;
    outline: none;
    width: 100%;
  }

  input,
  select,
  textarea,
  input[type="number"] {
    font-size: inherit;
    line-height: inherit;
  }

  textarea {
    height: auto;
    line-height: $lh;
    min-height: 8rem;
  }

  input[type="number"] {
    padding-right: 0;
  }

  input,
  select,
  option,
  textarea {
    @include trans;
    padding: 0.25rem 0.5rem;

    &.success {
      border-color: $success;
    }

    &.info {
      border-color: $info;
    }

    &:active,
    &:focus {
      background: $white;
      border-color: $info;
      box-shadow: $shadow-lg-info;
    }
  }

  .checkbox {
    color: $text-color;
    cursor: pointer;
    display: inline-block;

    input {
      width: auto;
    }

    .slider {
      display: none;
    }

    &.disabled {
      color: $text-color-light;
      cursor: not-allowed;

      label {
        color: $text-color-lighter;
      }
    }
  }

  .checkbox .isSwitch {
    display: inline-block;
    position: relative;

    label {
      height: 100%;
    }

    input {
      height: 0;
      opacity: 0;
      width: 2.5rem;

      &:checked {
        + .slider {
          background-color: $_switch-bg;
          box-shadow: 0 0 1px $_switch-bg;

          &::before {
            -webkit-transform: translateX(1rem);
            -ms-transform: translateX(1rem);
            transform: translateX(1rem);
          }
        }
      }
    }

    .slider {
      @include trans(0.2s);
      background-color: $grey-lighter;
      border-radius: $radius-xl;
      bottom: 0;
      cursor: pointer;
      display: block;
      height: 1.5rem;
      left: 0;
      line-height: inherit;
      position: absolute;
      right: 0;
      top: 0.5rem;
      width: 2.5rem;

      &::before {
        @include trans(0.2s);
        background-color: $white-bis;
        border-radius: $radius-round;
        bottom: 0.25rem;
        content: "";
        height: 1rem;
        left: 0.25rem;
        position: absolute;
        width: 1rem;
      }
    }
  }

  &.focus {
    border-color: $info;
    box-shadow: 0 0 2px $info-light;
  }

  &.type-checkbox,
  &.type-radio {
    @include clearfix;

    input {
      cursor: pointer;
      margin-right: 0.5rem;
    }
  }

  &.type-radio {
    input {
      margin-right: 0.75rem;
    }
  }

  &.type-select {
    label {
      line-height: 1rem;
      margin: 0.75rem 0 0;
      padding-left: 0.75rem;
    }
  }

  &.withIcon {
    padding-left: 3.5rem;
    padding-right: 0;
    // position: relative;

    input,
    select,
    textarea {
      border: none;
      display: block;

      &:active,
      &:focus {
        border-color: transparent;
        box-shadow: none;
      }
    }

    > .Icon {
      border-right: 1px solid $border-color;
      font-size: $h4;
      font-weight: $regular;
      height: 100%;
      left: 0;
      line-height: inherit;
      padding: 0.125rem 1rem;
      position: absolute;
      top: 0;
    }

    // modifiers
    &.trans {
      > .Icon {
        background: transparent;
        border: none;
      }
    }

    &.iconRight {
      padding-left: 0;
      padding-right: 2.75rem;

      > .Icon {
        border-right: 0;
        left: auto;
        right: 0;
      }
    }

    &.type-select {
      padding-left: 0;
      padding-right: 0;

      > .Icon {
        left: auto;
        padding: 0.375rem 0.5rem;
        pointer-events: none;
        position: absolute;
        right: 0;
      }
    }
  }

  // state
  &.disabled {
    input, textarea {
      background: $white-bis;
    }

    .checkbox .isSwitch .slider {
      background-color: $grey !important;
    }
  }

  &.valid {
    input,
    &.withIcon {
      border-color: $success;

      &:focus {
        box-shadow: $shadow-lg-success;
      }
    }
  }

  &.invalid {
    margin-bottom: 0;

    input,
    &.withIcon {
      border-color: $danger;

      &:focus {
        box-shadow: $shadow-lg-danger;
      }
    }
  }
}

.Field-cont {
  font-size: $h5;
  line-height: 2rem;
  margin-bottom: 1rem;
  @include md {
    font-size: $h5;
    line-height: 2.5rem;
  }

  &.ib {
    display: inline-block;
  }

  // sizes
  &.sm {
    line-height: 1.5rem;

    .Field .checkbox .isSwitch {
      input {
        width: 1.75rem;
      }

      .slider {
        height: 1rem;
        top: 0.25rem;
        width: 2rem;

        &::before {
          height: 0.5rem;
          width: 0.5rem;
        }
      }
    }
  }

  &.xs {
    font-size: $h6;
    line-height: 1.5rem;

    .Field.type-select > .Icon, .Field.type-text > .Icon {
      font-size: $h5;
      padding: 0;
    }

    input, select {
      padding-bottom: 0;
      padding-top: 0;
    }

    input {
      padding-right: 0.5rem;
    }

    .Field.withIcon {
      &.iconLeft {
        padding-left: 0.5rem;
      }

      &.iconRight {
        padding-right: 0.5rem;
      }
    }
  }
}

.Field-copy {
  bottom: 0.5rem;
  position: absolute;
  right: 1rem;
}

input[type=number] {
  -moz-appearance: textfield;
}

input[type=checkbox] {
  padding: 10px;
  transform: scale(1.25) translateX(2px);
  @include md {
    transform: scale(1.5) translateX(2px);
  }
}

input[type=radio] {
  padding: 10px;
  transform: scale(1.25) translateX(2px);
  @include md {
    transform: scale(1.5) translateX(2px);
  }
}
</style>
